import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChevronDownIcon,
  CircleStackIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  PlusIcon,
  TableCellsIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Combobox,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  SelectOption,
  Spinner,
  Textarea,
} from '../../../../components/ui'
import { useToasts } from '../../../../stores/useToasts'
import { useAuth } from '../../../../stores/useAuth'
import {
  useArchiveApprovedSowToDrive,
  useContractSlackRecipients,
  useHubSpotDeals,
  useHubSpotReadiness,
  useOperatingPeople,
  useRevenueModelSheetsSync,
  useSendContractSlackNotification,
  useUpdateApprovedSowSystem,
} from '../../api'
import {
  buildContextFromApprovedSow,
  findCitationForEditableField,
  parsePaymentScheduleRows,
  serializePaymentScheduleRows,
  type GmailSowAttachment,
  type OperatingWorkflowUpdate,
  type PaymentScheduleRow,
  type WorkflowRun,
} from '../../domain/contractWorkflow'
import { getErrorMessage } from '../../domain/formatters'
import type {
  ContractContext,
  ContractExtractionCitation,
  EditableApprovedSowData,
  HubSpotDeal,
} from '../../types'
import { GmailSowPdfPreviewPanel } from '../intake/SowIntakePanels'
import { ExtractionCitations } from './ExtractionCitations'

const revenueProfileOptions = ['Fixed Fee', 'Manual', 'Time and Expense', 'Retainer'] as const
const contractStatusOptions = ['Signed', 'MSA Signed', 'Complete', 'Assumption Needed'] as const
const allocationRoleOptions = [
  'FDE',
  'Lead FDE',
  'AI PM',
  'AI Architect',
  'CCO',
  'Chief Data Scientist',
  'Client Success VP',
  'Field CTO',
  'Ops Lead',
  'Product Owner',
  'Project Manager',
  'VP Biz Dev',
  'VP Change Enablement',
] as const

type EditableAllocationRow = {
  name?: string | null
  allocation?: string | null
  role?: string | null
  personId?: string | null
}

function parseEditableAllocationRows(value?: string): EditableAllocationRow[] {
  if (!value?.trim()) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter(row => row && typeof row === 'object') : []
  } catch {
    return []
  }
}

function serializeEditableAllocationRows(rows: EditableAllocationRow[]): string {
  return rows
    .map(row => {
      const name = row.name?.trim() || row.role?.trim() || 'FDE'
      const details = [row.allocation?.trim(), row.role?.trim() && row.role?.trim() !== name ? row.role.trim() : undefined]
        .filter(Boolean)
      return details.length ? `${name}: ${details.join(', ')}` : name
    })
    .join('\n')
}

function defaultAllocationForRole(role?: string | null): string {
  if (role === 'Lead FDE') return '20%'
  return allocationRoleOptions.includes(role as typeof allocationRoleOptions[number]) ? '100%' : ''
}

function allocationPercentValue(allocation?: string | null): string {
  const rawValue = allocation?.trim()
  if (!rawValue) return ''

  const asIntegerPercent = (value: string | number) => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return ''
    return String(Math.min(100, Math.max(0, Math.round(numericValue))))
  }

  const percentMatch = rawValue.match(/(\d+(?:\.\d+)?)\s*%/)
  if (percentMatch) return asIntegerPercent(percentMatch[1])

  const hoursPerWeekMatch = rawValue.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?)(?:\s*(?:a|per|\/)\s*week)?/i)
  if (hoursPerWeekMatch) {
    const percentage = Math.min(100, (Number(hoursPerWeekMatch[1]) / 40) * 100)
    return asIntegerPercent(percentage)
  }

  if (/\bhalf[\s-]?time\b/i.test(rawValue)) return '50'
  if (/\bfull[\s-]?time\b/i.test(rawValue)) return '100'
  if (/\bquarter[\s-]?time\b/i.test(rawValue)) return '25'
  if (/^\d+(?:\.\d+)?$/.test(rawValue)) {
    const numericValue = Number(rawValue)
    return asIntegerPercent(numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue)
  }
  return ''
}

function normalizeEditableAllocationRows(rows: EditableAllocationRow[]): EditableAllocationRow[] {
  return rows.map(row => {
    const percentage = allocationPercentValue(row.allocation)
    const extractedAllocation = row.allocation?.trim()
    return {
      ...row,
      allocation: percentage
        ? `${percentage}%`
        : extractedAllocation || defaultAllocationForRole(row.role),
    }
  })
}

function isFdeAllocationRole(role?: string | null): boolean {
  return role === 'FDE' || role === 'Lead FDE'
}

function fdeAllocationCount(rows: EditableAllocationRow[]): number {
  return rows.filter(row => isFdeAllocationRole(row.role)).length
}

function normalizeAllocationData(data: EditableApprovedSowData): EditableApprovedSowData {
  const parsedRows = parseEditableAllocationRows(data.fdeAllocationRows)
  const normalizedRows = normalizeEditableAllocationRows(parsedRows)
  const extractedFdeCount = Math.max(0, Math.floor(Number(data.numberOfFdes) || 0))
  const leadName = data.leadFde?.trim()
  if (leadName && !normalizedRows.some(row => row.role === 'Lead FDE')) {
    const namedLeadIndex = normalizedRows.findIndex(
      row => row.name?.trim().toLocaleLowerCase() === leadName.toLocaleLowerCase(),
    )
    if (namedLeadIndex >= 0) {
      normalizedRows[namedLeadIndex] = {
        ...normalizedRows[namedLeadIndex],
        role: 'Lead FDE',
        allocation: normalizedRows[namedLeadIndex].allocation || defaultAllocationForRole('Lead FDE'),
        personId: normalizedRows[namedLeadIndex].personId || data.leadFdePersonId || null,
      }
    } else {
      normalizedRows.unshift({
        name: leadName,
        personId: data.leadFdePersonId || null,
        role: 'Lead FDE',
        allocation: defaultAllocationForRole('Lead FDE'),
      })
    }
  }
  const missingFdeSlots = Math.max(0, extractedFdeCount - fdeAllocationCount(normalizedRows))
  normalizedRows.push(
    ...Array.from({ length: missingFdeSlots }, () => ({
      name: null,
      personId: null,
      role: 'FDE',
      allocation: defaultAllocationForRole('FDE'),
    })),
  )

  if (normalizedRows.length === 0) return data

  const fdeAllocationRows = JSON.stringify(normalizedRows)
  const numberOfFdes = String(fdeAllocationCount(normalizedRows))
  if (fdeAllocationRows === data.fdeAllocationRows && numberOfFdes === data.numberOfFdes) return data

  return {
    ...data,
    numberOfFdes,
    fdeAllocationRows,
    fdeAllocation: serializeEditableAllocationRows(normalizedRows),
  }
}

function isSameCitation(left?: ContractExtractionCitation, right?: ContractExtractionCitation): boolean {
  if (!left || !right) return false
  return left.field === right.field && left.page === right.page && left.snippet === right.snippet
}

function slackLink(url: unknown, label: string): string | undefined {
  if (typeof url !== 'string' || !url.startsWith('https://') || /[<>|]/.test(url)) return undefined
  return `<${url}|${label}>`
}

function slackText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function buildSlackReviewMessage(
  data: EditableApprovedSowData,
  context: { operatingProjectId?: string; operatingProjectUrl?: string; driveWebViewLink?: string } = {},
): string {
  const client = slackText(data.clientName.trim())
  const workstream = data.workstream?.trim() ? slackText(data.workstream.trim()) : undefined
  const engagement = workstream ? `${client} - ${workstream}` : client
  const lines = [`Contract Automation: ${engagement} is approved and ready for downstream review in Eliza.`]
  const operatingLink = slackLink(context.operatingProjectUrl, 'Open tentative project in Operating')
  if (operatingLink) {
    lines.push(operatingLink)
  } else if (context.operatingProjectId) {
    lines.push(`Operating tentative project ID: ${slackText(context.operatingProjectId)}`)
  }
  const driveLink = slackLink(context.driveWebViewLink, 'View signed SOW PDF')
  if (driveLink) lines.push(driveLink)
  return lines.join('\n')
}

export function EditableSowPanel({
  data,
  selectedRunLabel,
  citations = [],
  activeCitation,
  onCitationSelect,
  onChange,
}: {
  data: EditableApprovedSowData
  selectedRunLabel: string
  citations?: ContractExtractionCitation[]
  activeCitation?: ContractExtractionCitation
  onCitationSelect?: (citation: ContractExtractionCitation) => void
  onChange?: (data: EditableApprovedSowData) => void
}) {
  const [formData, setFormData] = useState(() => normalizeAllocationData(data))
  const [showCitations, setShowCitations] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const operatingPeople = useOperatingPeople()
  const extractionKey = `${selectedRunLabel}|${data.sowFile}`
  const extractedFdeCountRef = useRef({
    key: extractionKey,
    count: Math.max(0, Math.floor(Number(data.numberOfFdes) || 0)),
  })
  if (extractedFdeCountRef.current.key !== extractionKey) {
    extractedFdeCountRef.current = {
      key: extractionKey,
      count: Math.max(0, Math.floor(Number(data.numberOfFdes) || 0)),
    }
  }

  useEffect(() => {
    const normalizedData = normalizeAllocationData(data)
    setFormData(normalizedData)
    if (normalizedData !== data) onChange?.(normalizedData)
  }, [data, onChange])

  const updateFields = (updates: Partial<EditableApprovedSowData>) => {
    const next = { ...formData, ...updates }
    setFormData(next)
    onChange?.(next)
  }

  const updateField = (field: keyof EditableApprovedSowData, value: string) => {
    const updates: Partial<EditableApprovedSowData> = { [field]: value }
    if (field === 'fdeAllocation') updates.fdeAllocationRows = ''
    updateFields(updates)
  }

  const peopleOptions = useMemo(
    () => (operatingPeople.data?.people || []).map(person => ({ value: person.id, label: person.name })),
    [operatingPeople.data],
  )
  const peopleById = useMemo(
    () => new Map((operatingPeople.data?.people || []).map(person => [person.id, person])),
    [operatingPeople.data],
  )
  const allocationRows = useMemo(
    () => parseEditableAllocationRows(formData.fdeAllocationRows),
    [formData.fdeAllocationRows],
  )
  const hasAmbiguousAllocation = allocationRows.some(
    row => Boolean(row.allocation?.trim()) && !allocationPercentValue(row.allocation),
  )
  useEffect(() => {
    if (hasAmbiguousAllocation) setShowDetails(true)
  }, [hasAmbiguousAllocation])
  const currentFdeCount = fdeAllocationCount(allocationRows)
  const assignedFdeCount = allocationRows.filter(
    row => isFdeAllocationRole(row.role) && Boolean(row.personId),
  ).length
  const unassignedFdeCount = currentFdeCount - assignedFdeCount
  const additionalRoleCount = allocationRows.length - currentFdeCount
  const extractedFdeCount = extractedFdeCountRef.current.count

  const updateAllocationRows = (
    rows: EditableAllocationRow[],
    updates: Partial<EditableApprovedSowData> = {},
  ) => {
    updateFields({
      numberOfFdes: String(fdeAllocationCount(rows)),
      fdeAllocationRows: JSON.stringify(rows),
      fdeAllocation: serializeEditableAllocationRows(rows),
      ...updates,
    })
  }

  const selectAllocationPerson = (index: number, personId: string | null) => {
    const person = personId ? peopleById.get(personId) : undefined
    const nextRows = allocationRows.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            personId: person?.id || null,
            name: person?.name || (row.personId ? null : row.name),
            allocation: row.allocation || defaultAllocationForRole(row.role),
          }
        : row,
    )
    const selectedRow = nextRows[index]
    updateAllocationRows(
      nextRows,
      selectedRow?.role === 'Lead FDE'
        ? { leadFde: person?.name || '', leadFdePersonId: person?.id || '' }
        : {},
    )
  }

  const selectLeadPerson = (personId: string | null) => {
    const person = personId ? peopleById.get(personId) : undefined
    const leadIndex = allocationRows.findIndex(row => row.role === 'Lead FDE')
    const nextRows = allocationRows.map((row, index) =>
      index === leadIndex
        ? {
            ...row,
            personId: person?.id || null,
            name: person?.name || (row.personId ? null : row.name),
            allocation: row.allocation || defaultAllocationForRole('Lead FDE'),
          }
        : row,
    )
    if (person && leadIndex === -1) {
      nextRows.unshift({
        name: person.name,
        personId: person.id,
        role: 'Lead FDE',
        allocation: defaultAllocationForRole('Lead FDE'),
      })
    }
    updateAllocationRows(nextRows, {
      leadFdePersonId: person?.id || '',
      leadFde: person?.name || formData.leadFde,
    })
  }

  const selectAllocationRole = (index: number, role: string) => {
    const selectedRow = allocationRows[index]
    const nextRows = allocationRows.map((row, rowIndex) => {
      if (rowIndex === index) {
        return { ...row, role }
      }
      if (role === 'Lead FDE' && row.role === 'Lead FDE') {
        return { ...row, role: 'FDE' }
      }
      return row
    })
    updateAllocationRows(
      nextRows,
      role === 'Lead FDE'
        ? { leadFde: selectedRow?.name || '', leadFdePersonId: selectedRow?.personId || '' }
        : selectedRow?.role === 'Lead FDE'
          ? { leadFde: '', leadFdePersonId: '' }
        : {},
    )
  }

  const updateAllocationPercent = (index: number, value: string) => {
    if (value && (!/^\d{1,3}$/.test(value) || Number(value) > 100)) return
    const nextRows = allocationRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, allocation: `${value || '0'}%` } : row,
    )
    updateAllocationRows(nextRows)
  }

  const addAllocationRow = () => {
    updateAllocationRows([
      ...allocationRows,
      { name: null, personId: null, role: 'FDE', allocation: defaultAllocationForRole('FDE') },
    ])
  }

  const removeAllocationRow = (index: number) => {
    const removed = allocationRows[index]
    const nextRows = allocationRows.filter((_, rowIndex) => rowIndex !== index)
    updateAllocationRows(
      nextRows,
      removed?.role === 'Lead FDE'
        ? { leadFde: '', leadFdePersonId: '' }
        : {},
    )
  }

  const selectCitationForField = (field: keyof EditableApprovedSowData) => {
    const citation = findCitationForEditableField(citations, field)
    if (citation) onCitationSelect?.(citation)
  }

  const citationHandlers = (field: keyof EditableApprovedSowData) => ({
    onClick: () => selectCitationForField(field),
    onFocusCapture: () => selectCitationForField(field),
  })

  const paymentScheduleRows = useMemo(
    () => parsePaymentScheduleRows(formData.expectedPaymentSchedule),
    [formData.expectedPaymentSchedule],
  )

  const updatePaymentScheduleRows = (rows: PaymentScheduleRow[]) => {
    updateField('expectedPaymentSchedule', serializePaymentScheduleRows(rows))
  }

  const updatePaymentScheduleRow = (index: number, field: keyof PaymentScheduleRow, value: string) => {
    const nextRows = paymentScheduleRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    )
    updatePaymentScheduleRows(nextRows)
  }

  const addPaymentScheduleRow = () => {
    updatePaymentScheduleRows([...paymentScheduleRows, { timing: '', amount: '', description: '' }])
  }

  const removePaymentScheduleRow = (index: number) => {
    const nextRows = paymentScheduleRows.filter((_, rowIndex) => rowIndex !== index)
    updatePaymentScheduleRows(nextRows.length > 0 ? nextRows : [{ timing: '', amount: '', description: '' }])
  }

  const detailFieldClass = (layout = '') => `${showDetails ? '' : 'hidden'} order-4 min-w-0 ${layout}`

  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">Extracted contract fields</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Populated from the selected run. Edit fields before syncing downstream systems.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {citations.length > 0 && (
            <Button type="button" variant="secondary" size="sm" onClick={() => setShowCitations(value => !value)}>
              {showCitations ? 'Hide citations' : `View citations (${citations.length})`}
            </Button>
          )}
          <Badge variant="secondary" className="rounded-md">{selectedRunLabel}</Badge>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2">
        <div className="order-1 sm:col-span-2">
          <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Essential terms</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Review the core contract details before opening the full record.</p>
        </div>
        <div className="order-1 min-w-0" {...citationHandlers('clientName')}>
          <Label htmlFor="sow-client-name">Client</Label>
          <Input
            id="sow-client-name"
            className="mt-1"
            value={formData.clientName}
            onChange={event => updateField('clientName', event.target.value)}
          />
        </div>
        <div className={detailFieldClass()} data-testid="sow-file-field">
          <Label htmlFor="sow-file">SOW file</Label>
          <Input
            id="sow-file"
            className="mt-1"
            value={formData.sowFile}
            onChange={event => updateField('sowFile', event.target.value)}
          />
        </div>
        <div className={detailFieldClass()}>
          <Label htmlFor="sow-contract-id">Contract ID</Label>
          <Input
            id="sow-contract-id"
            className="mt-1"
            value={formData.contractId}
            onChange={event => updateField('contractId', event.target.value)}
            placeholder="Auto"
          />
        </div>
        <div className={detailFieldClass()}>
          <Label htmlFor="sow-contract-status">Contract status</Label>
          <Select
            value={formData.contractStatus}
            onValueChange={value => updateField('contractStatus', value)}
            placeholder="Signed"
            className="mt-1"
          >
            {contractStatusOptions.map(option => (
              <SelectOption key={option} value={option}>{option}</SelectOption>
            ))}
          </Select>
        </div>
        <div className={detailFieldClass('sm:col-span-2')} {...citationHandlers('workstream')}>
          <Label htmlFor="sow-workstream">Workstream</Label>
          <Input
            id="sow-workstream"
            className="mt-1"
            value={formData.workstream}
            onChange={event => updateField('workstream', event.target.value)}
            placeholder="AI enablement"
          />
        </div>
        <div className="order-1 min-w-0" {...citationHandlers('contractValue')}>
          <Label htmlFor="sow-contract-value">Contract value</Label>
          <Input
            id="sow-contract-value"
            className="mt-1"
            value={formData.contractValue}
            onChange={event => updateField('contractValue', event.target.value)}
            placeholder="216000"
          />
        </div>
        <div className="order-1 min-w-0" {...citationHandlers('contractLengthMonths')}>
          <Label htmlFor="sow-contract-length">Contract length (months)</Label>
          <Input
            id="sow-contract-length"
            className="mt-1"
            value={formData.contractLengthMonths}
            onChange={event => updateField('contractLengthMonths', event.target.value)}
            placeholder="12"
          />
        </div>
        <div className={detailFieldClass()} {...citationHandlers('totalHours')}>
          <Label htmlFor="sow-total-hours">Total hours</Label>
          <Input
            id="sow-total-hours"
            className="mt-1"
            value={formData.totalHours}
            onChange={event => updateField('totalHours', event.target.value)}
            placeholder="2080"
          />
        </div>
        <div className="order-1 min-w-0" {...citationHandlers('startDate')}>
          <Label htmlFor="sow-start-date">Start date</Label>
          <Input
            id="sow-start-date"
            className="mt-1"
            value={formData.startDate}
            onChange={event => updateField('startDate', event.target.value)}
            placeholder="YYYY-MM-DD"
          />
        </div>
        <div className={detailFieldClass()} {...citationHandlers('endDate')}>
          <Label htmlFor="sow-end-date">End date</Label>
          <Input
            id="sow-end-date"
            className="mt-1"
            value={formData.endDate}
            onChange={event => updateField('endDate', event.target.value)}
            placeholder="YYYY-MM-DD"
          />
        </div>
        <div className={detailFieldClass()}>
          <Label htmlFor="sow-close-date">Close date</Label>
          <Input
            id="sow-close-date"
            className="mt-1"
            value={formData.closeDate}
            onChange={event => updateField('closeDate', event.target.value)}
            placeholder="YYYY-MM-DD"
          />
        </div>
        <div className={detailFieldClass()} {...citationHandlers('paymentTermsDays')}>
          <Label htmlFor="sow-payment-terms-days">Payment terms days</Label>
          <Input
            id="sow-payment-terms-days"
            className="mt-1"
            value={formData.paymentTermsDays}
            onChange={event => updateField('paymentTermsDays', event.target.value)}
            placeholder="30"
          />
        </div>
        <div className={detailFieldClass()} {...citationHandlers('numberOfFdes')}>
          <Label htmlFor="sow-number-fdes">Number of FDEs</Label>
          <Input
            id="sow-number-fdes"
            className="mt-1"
            value={formData.numberOfFdes}
            readOnly
            aria-describedby="sow-number-fdes-help"
          />
          <p id="sow-number-fdes-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Updates automatically from Lead FDE and FDE staffing rows.
          </p>
        </div>
        <div className={detailFieldClass()} {...citationHandlers('leadFde')}>
          <Label htmlFor="sow-lead-fde">Lead FDE</Label>
          <Input
            id="sow-lead-fde"
            className="mt-1"
            value={formData.leadFde}
            onChange={event => updateFields({ leadFde: event.target.value, leadFdePersonId: '' })}
            placeholder="Lead engineer"
          />
        </div>
        <div className={detailFieldClass()}>
          <Label>Operating lead identity</Label>
          <Combobox
            className="mt-1"
            options={peopleOptions}
            value={formData.leadFdePersonId || null}
            onChange={selectLeadPerson}
            placeholder={operatingPeople.isLoading ? 'Loading Operating people...' : 'Select Operating person'}
            searchPlaceholder="Search Operating people..."
            emptyMessage="No Operating people found."
            disabled={operatingPeople.isLoading || operatingPeople.isError || peopleOptions.length === 0}
          />
          {operatingPeople.isError && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
              Operating people could not be loaded. The reviewed name will remain unassigned unless it exactly matches the directory.
            </p>
          )}
          {!operatingPeople.isLoading && !operatingPeople.isError && peopleOptions.length === 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No people are available from this tenant's Operating connector.</p>
          )}
        </div>
        <div className={detailFieldClass('sm:col-span-2')}>
          <Label htmlFor="sow-bd-lead">BD Lead</Label>
          <Input
            id="sow-bd-lead"
            className="mt-1"
            value={formData.bdLead}
            onChange={event => updateField('bdLead', event.target.value)}
          />
        </div>
        <div className={detailFieldClass()}>
          <Label htmlFor="sow-hubspot-stage">HubSpot stage</Label>
          <Input
            id="sow-hubspot-stage"
            className="mt-1"
            value={formData.hubspotStage}
            onChange={event => updateField('hubspotStage', event.target.value)}
          />
        </div>
        <div className={detailFieldClass()} {...citationHandlers('revenueProfile')}>
          <Label htmlFor="sow-revenue-profile">Revenue profile</Label>
          <Select
            value={formData.revenueProfile}
            onValueChange={value => updateField('revenueProfile', value)}
            placeholder="Select revenue profile"
            className="mt-1"
          >
            {revenueProfileOptions.map(option => (
              <SelectOption key={option} value={option}>{option}</SelectOption>
            ))}
          </Select>
        </div>
        <div className={detailFieldClass('sm:col-span-2')} {...citationHandlers('fdeAllocation')}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Label>FDE team allocations</Label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select an Operating person, role, and planned allocation for each position.
              </p>
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={addAllocationRow}>
              <PlusIcon className="h-4 w-4" />
              Add team member
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="secondary">Extracted requirement: {extractedFdeCount}</Badge>
            <Badge variant={currentFdeCount === extractedFdeCount ? 'success' : 'warning'}>
              Operating plan: {currentFdeCount}
            </Badge>
            <Badge variant="secondary">{assignedFdeCount} assigned</Badge>
            <Badge variant={unassignedFdeCount > 0 ? 'warning' : 'success'}>
              {unassignedFdeCount} unassigned
            </Badge>
            {additionalRoleCount > 0 && (
              <Badge variant="secondary">{additionalRoleCount} additional role{additionalRoleCount === 1 ? '' : 's'}</Badge>
            )}
          </div>
          {currentFdeCount !== extractedFdeCount && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              The staffing plan differs from the extracted requirement. Operating will use the {currentFdeCount} Lead FDE/FDE row{currentFdeCount === 1 ? '' : 's'} below.
            </p>
          )}

          <div className="relative mt-3 rounded-lg border border-gray-200 dark:border-dark-border">
            <div className="hidden grid-cols-[minmax(0,1fr)_180px_110px_40px] gap-3 rounded-t-lg bg-gray-50 px-4 py-2 text-xs font-medium uppercase text-gray-500 lg:grid dark:bg-dark-bg dark:text-gray-400">
              <span>Operating person</span>
              <span>Role</span>
              <span>Allocation</span>
              <span className="sr-only">Actions</span>
            </div>
            {allocationRows.length === 0 ? (
              <div className="px-4 py-5 text-sm text-gray-500 dark:text-gray-400">
                No team allocations were extracted. Add a position to prepare Operating staffing.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-dark-border/70">
                {allocationRows.map((row, index) => (
                  <div
                    key={`allocation-row-${index}`}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] items-end gap-3 bg-white p-4 lg:grid-cols-[minmax(0,1fr)_180px_110px_40px] lg:items-center dark:bg-dark-surface"
                  >
                    <div className="col-span-3 min-w-0 lg:col-span-1">
                      <span className="mb-1 block text-xs font-medium text-gray-500 lg:hidden dark:text-gray-400">Operating person</span>
                      <Combobox
                        options={peopleOptions}
                        value={row.personId || null}
                        onChange={personId => selectAllocationPerson(index, personId)}
                        placeholder={row.name ? `Match ${row.name}` : 'Select Operating person'}
                        searchPlaceholder="Search Operating people..."
                        emptyMessage="No Operating people found."
                        disabled={operatingPeople.isLoading || operatingPeople.isError || peopleOptions.length === 0}
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="mb-1 block text-xs font-medium text-gray-500 lg:hidden dark:text-gray-400">Role</span>
                      <Select
                        className="min-w-0"
                        value={row.role || ''}
                        onValueChange={role => selectAllocationRole(index, role)}
                        placeholder="Select role"
                      >
                        {allocationRoleOptions.map(role => (
                          <SelectOption key={role} value={role}>{role}</SelectOption>
                        ))}
                      </Select>
                    </div>
                    <div className="relative">
                      <span className="mb-1 block text-xs font-medium text-gray-500 lg:hidden dark:text-gray-400">Allocation</span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        className="pr-8"
                        value={allocationPercentValue(row.allocation)}
                        onChange={event => updateAllocationPercent(index, event.target.value)}
                        aria-label={`Allocation percentage for team member ${index + 1}`}
                        placeholder={row.role === 'Lead FDE' ? '20' : '100'}
                      />
                      <span className="pointer-events-none absolute bottom-0 right-3 flex h-10 items-center text-sm text-gray-400 dark:text-gray-500">%</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="mb-0.5"
                      onClick={() => removeAllocationRow(index)}
                      aria-label={`Remove team allocation ${index + 1}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {operatingPeople.isError && (
            <p className="mt-2 text-xs text-rose-600 dark:text-rose-300">
              Operating people could not be loaded. Existing extracted names remain unassigned until a directory match is available.
            </p>
          )}
        </div>
        <div className={detailFieldClass('sm:col-span-2')} {...citationHandlers('expectedPaymentSchedule')}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Label htmlFor="sow-payment-schedule-row-0-timing">Expected payment schedule</Label>
            <Button type="button" variant="secondary" size="sm" onClick={addPaymentScheduleRow}>
              <PlusIcon className="h-4 w-4" />
              Add row
            </Button>
          </div>
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
            <div className="hidden min-w-[640px] grid-cols-[minmax(120px,0.9fr)_minmax(120px,0.7fr)_minmax(180px,1.4fr)_48px] bg-gray-50 sm:grid px-3 py-2 text-xs font-medium uppercase text-gray-500 dark:bg-dark-bg dark:text-gray-400">
              <span>Timing</span>
              <span>Amount</span>
              <span>Description</span>
              <span className="sr-only">Actions</span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-dark-border/70">
              {paymentScheduleRows.map((row, index) => (
                <div
                  key={`payment-row-${index}`}
                  className="grid min-w-0 grid-cols-1 gap-2 bg-white sm:min-w-[640px] sm:grid-cols-[minmax(120px,0.9fr)_minmax(120px,0.7fr)_minmax(180px,1.4fr)_48px] p-3 dark:bg-dark-surface"
                  {...citationHandlers('expectedPaymentSchedule')}
                >
                  <Input
                    id={`sow-payment-schedule-row-${index}-timing`}
                    value={row.timing}
                    onChange={event => updatePaymentScheduleRow(index, 'timing', event.target.value)}
                    placeholder="Due at signing"
                  />
                  <Input
                    value={row.amount}
                    onChange={event => updatePaymentScheduleRow(index, 'amount', event.target.value)}
                    placeholder="$18,000"
                  />
                  <Input
                    value={row.description}
                    onChange={event => updatePaymentScheduleRow(index, 'description', event.target.value)}
                    placeholder="Monthly payment, Net 30"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removePaymentScheduleRow(index)}
                    aria-label={`Remove payment schedule row ${index + 1}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={detailFieldClass('sm:col-span-2')} {...citationHandlers('notes')}>
          <Label htmlFor="sow-notes">Notes</Label>
          <Textarea
            id="sow-notes"
            className="mt-1 min-h-[96px]"
            value={formData.notes}
            onChange={event => updateField('notes', event.target.value)}
          />
        </div>
        <div className="order-2 sm:col-span-2">
          <Button
            type="button"
            variant="secondary"
            className="w-full justify-center border-dashed py-3"
            onClick={() => setShowDetails(value => !value)}
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide additional contract details' : 'Show additional contract details'}
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        {showDetails && (
          <div className="order-3 border-t border-gray-200 pt-5 sm:col-span-2 dark:border-dark-border">
            <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Additional contract details</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Delivery, staffing, system, and payment information remains fully editable.</p>
          </div>
        )}
      </div>
      {showCitations && (
        <ExtractionCitations
          citations={citations}
          activeCitation={activeCitation}
          onCitationSelect={onCitationSelect}
        />
      )}
    </section>
  )
}

export function SowExtractionReviewSection({
  selectedSowData,
  selectedRun,
  citations,
  onChange,
  attachment,
  previewBlob,
  previewLoading,
  previewError,
}: {
  selectedSowData?: EditableApprovedSowData
  selectedRun?: WorkflowRun
  citations: ContractExtractionCitation[]
  onChange: (data: EditableApprovedSowData) => void
  attachment?: GmailSowAttachment
  previewBlob?: Blob
  previewLoading: boolean
  previewError: unknown
}) {
  const [activeCitation, setActiveCitation] = useState<ContractExtractionCitation | undefined>()

  useEffect(() => {
    if (!activeCitation) return
    if (!citations.some(citation => isSameCitation(citation, activeCitation))) {
      setActiveCitation(undefined)
    }
  }, [activeCitation, citations])

  if (!selectedSowData || !selectedRun) return null

  const content = (
    <EditableSowPanel
      data={selectedSowData}
      selectedRunLabel={selectedRun.label}
      citations={citations}
      activeCitation={activeCitation}
      onCitationSelect={setActiveCitation}
      onChange={onChange}
    />
  )

  if (!attachment) return content

  return (
    <div className="grid min-w-0 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0">{content}</div>
      <div className="min-w-0 xl:sticky xl:top-4">
        <GmailSowPdfPreviewPanel
          attachment={attachment}
          previewBlob={previewBlob}
          isLoading={previewLoading}
          error={previewError}
          activeCitation={activeCitation}
        />
      </div>
    </div>
  )
}

export function SOWDistributionPanel({
  approvedData,
  approvedContext,
  hubspotDeals,
  sourceAttachment,
  onApprovedDataChange,
  onOperatingUpdate,
}: {
  approvedData: EditableApprovedSowData
  approvedContext?: ContractContext
  hubspotDeals: HubSpotDeal[]
  sourceAttachment?: GmailSowAttachment
  onApprovedDataChange: (data: EditableApprovedSowData) => void
  onOperatingUpdate?: (update: OperatingWorkflowUpdate) => void
}) {
  const pushToast = useToasts(state => state.push)
  const { hasAnyPermission } = useAuth()
  const canWrite = hasAnyPermission(['contract_automation:write', 'platform:admin'])
  const driveArchive = useArchiveApprovedSowToDrive()
  const hubspotUpdate = useUpdateApprovedSowSystem('hubspot')
  const operatingUpdate = useUpdateApprovedSowSystem('operating')
  const slackNotify = useSendContractSlackNotification()
  const slackRecipients = useContractSlackRecipients()
  const syncSheets = useRevenueModelSheetsSync()
  const hubspotReadiness = useHubSpotReadiness()
  const hubspotReady = hubspotReadiness.data?.available === true
  const hubspotClientName = approvedData.clientName?.trim() || ''
  const hubspotDealsQuery = useHubSpotDeals(
    hubspotClientName,
    hubspotReady && Boolean(hubspotClientName),
  )
  const [valuesApproved, setValuesApproved] = useState(false)
  const [hubspotConfirmationOpen, setHubspotConfirmationOpen] = useState(false)
  const [selectedSlackRecipients, setSelectedSlackRecipients] = useState<string[]>([])
  const slackRecipientsInitialized = useRef(false)
  const sheetsAssignedContractId = useRef<string | undefined>(undefined)
  const hubspotDealSource = useMemo(
    () => hubspotReady && hubspotClientName
      ? (hubspotDealsQuery.data?.deals || [])
      : hubspotDeals,
    [hubspotClientName, hubspotDeals, hubspotDealsQuery.data?.deals, hubspotReady],
  )
  const hubspotDealOptions = useMemo(
    () => hubspotDealSource
      .filter((deal): deal is HubSpotDeal & { id: string } => Boolean(deal.id))
      .map(deal => ({
        id: deal.id,
        name: deal.deal_name?.trim() || 'Unnamed deal',
        label: `${deal.deal_name?.trim() || 'Unnamed deal'} · ID ${deal.id}`,
      })),
    [hubspotDealSource],
  )
  const selectedHubSpotDeal = useMemo(
    () => hubspotDealOptions.find(deal => deal.id === approvedData.hubspotDealId),
    [approvedData.hubspotDealId, hubspotDealOptions],
  )
  const hubspotMode = hubspotReadiness.data?.mode
  const hubspotTarget = hubspotReadiness.data?.target
  const hubspotReadinessReason = hubspotReadiness.isLoading
    ? 'Checking HubSpot safeguards...'
    : hubspotReadiness.isError
      ? 'HubSpot readiness could not be verified. No update will be sent.'
      : hubspotReadiness.data?.reason || 'HubSpot updates are unavailable.'

  useEffect(() => {
    if (!slackRecipientsInitialized.current && slackRecipients.data?.default_recipient) {
      slackRecipientsInitialized.current = true
      setSelectedSlackRecipients([slackRecipients.data.default_recipient])
    }
  }, [slackRecipients.data?.default_recipient])

  const operatingDetails = operatingUpdate.data?.details
  const operatingProjectId = typeof operatingDetails?.projectId === 'string'
    ? operatingDetails.projectId
    : undefined
  const operatingProjectUrl = typeof operatingDetails?.projectUrl === 'string'
    ? operatingDetails.projectUrl
    : undefined
  const operatingOpenUrl = operatingProjectUrl || 'https://use.operating.app/projects'
  const driveWebViewLink = typeof driveArchive.data?.details?.webViewLink === 'string'
    ? driveArchive.data.details.webViewLink
    : undefined
  const staffingStatus = typeof operatingDetails?.staffingStatus === 'string'
    ? operatingDetails.staffingStatus
    : ''
  const unmappedStaff = typeof operatingDetails?.unmappedStaff === 'string'
    ? operatingDetails.unmappedStaff.trim()
    : ''
  const reviewedStaffNames = [
    approvedData.leadFde,
    ...parseEditableAllocationRows(approvedData.fdeAllocationRows).map(row => row.name),
  ]
    .map(name => name?.trim())
    .filter((name): name is string => Boolean(name))
  const unresolvedOperatingStaff = unmappedStaff || (
    staffingStatus && staffingStatus !== 'Assigned' && reviewedStaffNames.length > 0
      ? Array.from(new Set(reviewedStaffNames)).join(', ')
      : ''
  )
  const approvalValuesFingerprint = useMemo(
    () => JSON.stringify({
      ...approvedData,
      contractId: !approvedData.contractId || approvedData.contractId === sheetsAssignedContractId.current
        ? undefined
        : approvedData.contractId,
      hubspotDealId: undefined,
    }),
    [approvedData],
  )
  const resetDriveArchive = driveArchive.reset
  const resetHubspotUpdate = hubspotUpdate.reset
  const resetOperatingUpdate = operatingUpdate.reset
  const resetSheetsSync = syncSheets.reset
  const resetSlackNotify = slackNotify.reset

  useEffect(() => {
    setValuesApproved(false)
    setHubspotConfirmationOpen(false)
    resetDriveArchive()
    resetHubspotUpdate()
    resetOperatingUpdate()
    resetSheetsSync()
    resetSlackNotify()
    onOperatingUpdate?.({ status: 'idle' })
  }, [
    approvalValuesFingerprint,
    onOperatingUpdate,
    resetDriveArchive,
    resetHubspotUpdate,
    resetOperatingUpdate,
    resetSheetsSync,
    resetSlackNotify,
  ])

  const ambiguousAllocationRows = useMemo(
    () => parseEditableAllocationRows(approvedData.fdeAllocationRows).filter(
      row => Boolean(row.allocation?.trim()) && !allocationPercentValue(row.allocation),
    ),
    [approvedData.fdeAllocationRows],
  )
  const hasAmbiguousAllocations = ambiguousAllocationRows.length > 0

  const triggerDriveArchive = () => {
    if (!sourceAttachment?.local_path) {
      pushToast({ kind: 'warning', message: 'Parse a downloaded SOW PDF before accepting it into Drive.' })
      return
    }
    driveArchive.mutate(
      {
        approvedData,
        localPath: sourceAttachment.local_path,
        filename: sourceAttachment.filename,
      },
      {
        onSuccess: result => pushToast({ kind: 'success', message: result.message }),
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const triggerHubSpot = () => {
    if (!hubspotReady) {
      pushToast({ kind: 'warning', message: hubspotReadinessReason })
      return
    }
    if (!selectedHubSpotDeal || !hubspotTarget) {
      pushToast({ kind: 'warning', message: 'Select the existing HubSpot deal to update.' })
      return
    }
    setHubspotConfirmationOpen(true)
  }

  const confirmHubSpotUpdate = () => {
    if (!selectedHubSpotDeal || !hubspotTarget) return
    hubspotUpdate.mutate(
      {
        approvedData,
        confirmation: {
          deal_id: selectedHubSpotDeal.id,
          deal_name: selectedHubSpotDeal.name,
          target: hubspotTarget,
          confirmed: true,
        },
      },
      {
        onSuccess: result => {
          setHubspotConfirmationOpen(false)
          pushToast({ kind: 'success', message: result.message })
        },
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const triggerOperating = () => {
    onOperatingUpdate?.({ status: 'pending' })
    operatingUpdate.mutate(
      { approvedData },
      {
        onSuccess: result => {
          onOperatingUpdate?.({ status: 'completed', message: result.message })
          pushToast({ kind: 'success', message: result.message })
        },
        onError: error => {
          const message = getErrorMessage(error)
          onOperatingUpdate?.({ status: 'failed', message })
          pushToast({ kind: 'error', message })
        },
      },
    )
  }

  const triggerSheets = () => {
    const contextForSync = approvedContext || buildContextFromApprovedSow(approvedData)
    if (!approvedData.clientName) {
      pushToast({ kind: 'warning', message: 'Approve or extract SOW data before syncing Sheets.' })
      return
    }
    syncSheets.mutate(
      {
        context: contextForSync,
        approvedData,
      },
      {
        onSuccess: result => {
          sheetsAssignedContractId.current = result.contract_id || undefined
          if (result.contract_id && result.contract_id !== approvedData.contractId) {
            onApprovedDataChange({ ...approvedData, contractId: result.contract_id })
          }
          pushToast({ kind: 'success', message: `Updated ${result.sheet_name} in Google Sheets.` })
        },
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const triggerSlack = () => {
    if (selectedSlackRecipients.length === 0) {
      pushToast({ kind: 'warning', message: 'Select at least one approved Slack recipient.' })
      return
    }
    slackNotify.mutate(
      {
        message: buildSlackReviewMessage(approvedData, {
          operatingProjectId,
          operatingProjectUrl,
          driveWebViewLink,
        }),
        recipientEmails: selectedSlackRecipients,
      },
      {
        onSuccess: result => pushToast({
          kind: 'success',
          message: `Sent Slack DM to ${(result.recipients || [result.recipient]).join(', ')}.`,
        }),
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const systems = [
    {
      key: 'drive',
      label: 'Accept and Add to Drive',
      icon: CloudArrowUpIcon,
      status: driveArchive.data?.status,
      message: driveArchive.data?.message,
      pending: driveArchive.isPending,
      onClick: triggerDriveArchive,
      disabled: !sourceAttachment?.local_path,
      unavailable: false,
      unavailableReason: undefined,
      disabledReason: sourceAttachment?.local_path
        ? undefined
        : 'Parse a downloaded SOW PDF first.',
      details: driveArchive.data?.details,
    },
    {
      key: 'hubspot',
      label: 'Update HubSpot',
      icon: CircleStackIcon,
      status: hubspotUpdate.data?.status,
      message: hubspotUpdate.data?.message,
      pending: hubspotUpdate.isPending,
      onClick: triggerHubSpot,
      disabled: !selectedHubSpotDeal,
      unavailable: !hubspotReady,
      unavailableReason: hubspotReady ? undefined : hubspotReadinessReason,
      disabledReason: selectedHubSpotDeal
        ? undefined
        : 'Select an existing Contract Sent or Closed Won HubSpot deal.',
    },
    {
      key: 'operating',
      label: 'Update Operating',
      icon: Cog6ToothIcon,
      status: operatingUpdate.data?.status,
      message: operatingUpdate.data?.message,
      pending: operatingUpdate.isPending,
      onClick: triggerOperating,
      disabled: hasAmbiguousAllocations,
      unavailable: false,
      unavailableReason: undefined,
      disabledReason: hasAmbiguousAllocations
        ? 'Enter a valid 0-100% allocation for each staffing row.'
        : undefined,
    },
    {
      key: 'sheets',
      label: 'Update Revenue Model',
      icon: TableCellsIcon,
      status: syncSheets.data ? 'completed' : undefined,
      message: syncSheets.data ? `Updated ${syncSheets.data.sheet_name}` : undefined,
      pending: syncSheets.isPending,
      onClick: triggerSheets,
      disabled: Boolean(syncSheets.data),
      unavailable: false,
      unavailableReason: undefined,
      disabledReason: syncSheets.data
        ? 'Revenue model already updated for this review.'
        : undefined,
    },
    {
      key: 'slack',
      label: 'Notify Slack Reviewer',
      icon: ChatBubbleOvalLeftEllipsisIcon,
      status: slackNotify.data ? 'completed' : undefined,
      message: slackNotify.data ? `Sent to ${slackNotify.data.recipient}` : undefined,
      pending: slackNotify.isPending,
      onClick: triggerSlack,
      disabled: selectedSlackRecipients.length === 0,
      unavailable: slackRecipients.isError,
      unavailableReason: slackRecipients.isError
        ? 'Slack recipients could not be loaded. Retry before sending.'
        : undefined,
      disabledReason: slackRecipients.isLoading
        ? 'Loading approved Slack recipients...'
        : selectedSlackRecipients.length > 0
        ? undefined
        : 'Select at least one approved Slack recipient.',
    },
  ]

  const formatDetails = (details?: Record<string, unknown>): Array<[string, unknown]> => {
    if (!details) return []
    const rows: Array<[string, unknown]> = [
      ['Total hours', details.totalHours],
      ['Hours/day', details.hoursPerDay],
      ['Working days', details.workingDays],
      ['Staffing', details.staffingStatus],
      ['Assigned people', details.staffingAssigned !== undefined && details.staffingNamed !== undefined ? `${details.staffingAssigned}/${details.staffingNamed}` : undefined],
      ['Unmapped staff', details.unmappedStaff],
      ['Project status', details.projectStatusName],
      ['Allocation status', details.allocationStatusName],
      ['Drive file', details.fileName],
    ]
    return rows.filter(([, value]) => value !== undefined && value !== null && value !== '')
  }
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">Approve and distribute</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Trigger each downstream system from the reviewed SOW fields.
          </p>
        </div>
        <Badge variant={approvedData.clientName ? 'success' : 'secondary'} className="rounded-md">
          {approvedData.clientName ? 'Approval data loaded' : 'Waiting for extraction'}
        </Badge>
      </div>

      <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
        <Checkbox
          aria-label="Approve values"
          checked={valuesApproved}
          onChange={event => setValuesApproved(event.target.checked)}
          label="Approve values"
        />
      </div>

      {!canWrite && (
        <Alert variant="warning" title="Read-only access" className="mt-4">
          Contract Automation write permission is required to update downstream systems.
        </Alert>
      )}

      {unresolvedOperatingStaff && (
        <Alert variant="warning" title="Operating staff needs review" className="mt-4">
          Couldn&apos;t match to Operating: <strong>{unresolvedOperatingStaff}</strong>. Pick the person from the Operating dropdown (full name required) and re-run.
        </Alert>
      )}

      {hasAmbiguousAllocations && (
        <Alert variant="warning" title="Allocation needs review" className="mt-4">
          Enter a percentage from 0 to 100 for each staffing row before updating Operating.
        </Alert>
      )}

      <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {systems.map(system => {
          const Icon = system.icon
          const completed = system.status === 'completed'
          const needsInput = canWrite && !completed && !system.unavailable && system.disabled
          return (
            <div
              key={system.key}
              role="group"
              aria-label={`${system.label} integration`}
              className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-bg"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-500 dark:bg-dark-surface dark:text-gray-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-charcoal dark:text-gray-100">{system.label}</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {!canWrite
                        ? 'Read-only access'
                        : completed
                        ? 'Completed from approved SOW'
                        : system.unavailable
                          ? system.unavailableReason || 'Unavailable'
                          : needsInput
                            ? system.disabledReason || 'Needs reviewer input'
                          : system.key === 'drive'
                            ? 'Stores accepted PDF'
                            : 'Ready after approval'}
                    </p>
                  </div>
                </div>
                <Badge variant={completed ? 'success' : 'secondary'} className="rounded-md">
                  {!canWrite ? 'Read only' : completed ? 'Done' : system.unavailable ? 'Unavailable' : needsInput ? 'Needs input' : 'Ready'}
                </Badge>
              </div>
              {system.key === 'hubspot' && (
                <div className="mt-4">
                  <Label className="text-xs">Existing HubSpot deal</Label>
                  <Select
                    className="mt-2"
                    value={approvedData.hubspotDealId || ''}
                    onValueChange={value => onApprovedDataChange({
                      ...approvedData,
                      hubspotDealId: value === '__none__' ? '' : value,
                    })}
                    placeholder={
                      hubspotDealsQuery.isLoading
                        ? 'Loading eligible HubSpot deals...'
                        : hubspotDealsQuery.isError
                          ? 'Could not load HubSpot deals'
                          : hubspotDealOptions.length > 0
                            ? 'Select the deal to update'
                            : 'No matching Contract Sent or Closed Won deals available'
                    }
                    disabled={
                      !hubspotReady
                      || hubspotDealsQuery.isLoading
                      || hubspotDealsQuery.isError
                      || hubspotDealOptions.length === 0
                    }
                  >
                    <SelectOption value="__none__">No deal selected</SelectOption>
                    {hubspotDealOptions.map(deal => (
                      <SelectOption key={deal.id} value={deal.id}>
                        {deal.label}
                      </SelectOption>
                    ))}
                  </Select>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {hubspotDealsQuery.isError
                      ? 'Eligible HubSpot deals could not be loaded. Check the configured HubSpot connection and retry.'
                      : hubspotDealOptions.length > 0
                        ? 'Choose the exact client-related deal to move to Closed Won or safely retry. Contract Automation will not create a production deal.'
                        : 'No client-related deals are available in the configured Contract Sent or Closed Won stages.'}
                  </p>
                </div>
              )}
              <Button
                type="button"
                variant="brand"
                className="mt-4 w-full justify-center"
                onClick={system.onClick}
                disabled={!canWrite || !valuesApproved || !approvedData.clientName || system.pending || system.disabled || system.unavailable}
              >
                {system.pending ? <Spinner size="sm" /> : <Icon className="h-4 w-4" />}
                {system.label}
              </Button>
              {system.key === 'drive' && driveWebViewLink && (
                <a
                  href={driveWebViewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-eliza-red hover:underline"
                >
                  Open in Drive
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
              {system.key === 'operating' && completed && (
                <a
                  href={operatingOpenUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-eliza-red hover:underline"
                >
                  Open in Operating
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
              {system.key === 'sheets' && syncSheets.data?.spreadsheet_url && (
                <a
                  href={syncSheets.data.spreadsheet_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-eliza-red hover:underline"
                >
                  Open in Sheets
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              )}
              {system.key === 'slack' && (
                <div className="mt-3">
                  <Label className="text-xs">Recipients</Label>
                  {slackRecipients.isLoading && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Spinner size="sm" />
                      Loading approved recipients...
                    </div>
                  )}
                  {slackRecipients.isError && (
                    <Alert variant="error" className="mt-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span>Slack recipients could not be loaded.</span>
                        <Button type="button" variant="secondary" size="sm" onClick={() => slackRecipients.refetch()}>
                          Retry
                        </Button>
                      </div>
                    </Alert>
                  )}
                  <div className="mt-2 space-y-1.5">
                    {(slackRecipients.data?.recipients || []).map(email => (
                      <Checkbox
                        key={email}
                        aria-label={`Notify ${email}`}
                        checked={selectedSlackRecipients.includes(email)}
                        disabled={slackRecipients.isLoading || slackRecipients.isError}
                        onChange={() => setSelectedSlackRecipients(current => current.includes(email)
                          ? current.filter(value => value !== email)
                          : [...current, email])}
                        label={email}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Select one or more tenant-approved people. Private channel delivery is temporarily unavailable.
                  </p>
                </div>
              )}
              {system.message && (
                <p className="mt-3 rounded-md bg-white px-3 py-2 text-xs text-gray-600 dark:bg-dark-surface dark:text-gray-300">
                  {system.message}
                </p>
              )}
              {formatDetails(system.key === 'operating' ? operatingUpdate.data?.details : system.key === 'drive' ? driveArchive.data?.details : undefined).length > 0 && (
                <dl className="mt-3 grid grid-cols-2 gap-2 rounded-md bg-white p-3 text-xs dark:bg-dark-surface">
                  {formatDetails(system.key === 'operating' ? operatingUpdate.data?.details : driveArchive.data?.details).map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                      <dd className="mt-0.5 truncate font-medium text-charcoal dark:text-gray-100">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )
        })}
      </div>

      <Modal open={hubspotConfirmationOpen} onClose={() => setHubspotConfirmationOpen(false)}>
        <ModalContent size="lg">
          <ModalHeader>
            <ModalTitle>Confirm HubSpot update</ModalTitle>
            <ModalDescription>
              Review the exact existing deal before sending this one update.
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
              <div>
                <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Write mode</p>
                <p className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">
                  {hubspotMode === 'production_test'
                    ? 'Controlled production test'
                    : hubspotMode === 'production'
                      ? 'Production'
                      : 'Mock portal'}
                </p>
              </div>
              <Badge variant={hubspotMode === 'production' ? 'warning' : 'secondary'} className="rounded-md">
                {hubspotTarget === 'production' ? 'Production portal' : 'Mock portal'}
              </Badge>
            </div>

            <dl className="grid gap-3 rounded-md border border-gray-200 p-4 text-sm dark:border-dark-border">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Existing deal</dt>
                <dd className="mt-1 font-semibold text-charcoal dark:text-gray-100">
                  {selectedHubSpotDeal?.name}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Deal ID</dt>
                <dd className="mt-1 font-mono text-charcoal dark:text-gray-100">
                  {selectedHubSpotDeal?.id}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Update</dt>
                <dd className="mt-1 text-charcoal dark:text-gray-100">
                  Move this deal to Closed Won using the approved SOW values.
                  {hubspotMode === 'production_test' && ' The amount is forced to $0 for this test.'}
                </dd>
              </div>
            </dl>

            <Alert variant="warning" title="Existing deal only">
              This action updates the selected deal and does not create a new production deal.
              The server will reject a changed ID or name before writing.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setHubspotConfirmationOpen(false)}
              disabled={hubspotUpdate.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={confirmHubSpotUpdate}
              disabled={hubspotUpdate.isPending}
            >
              {hubspotUpdate.isPending && <Spinner size="sm" />}
              Confirm and update HubSpot
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  )
}
